'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SearchBarProps {
  onSearch: (query: string) => void;
  initialValue: string;
}

const SearchBar = ({ onSearch, initialValue }: SearchBarProps) => {
  const [searchQuery, setSearchQuery] = useState(initialValue);
  const [isVoiceSearchActive, setIsVoiceSearchActive] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(searchQuery);
  };

  const handleVoiceSearch = () => {
    setIsVoiceSearchActive(true);
    setTimeout(() => {
      setIsVoiceSearchActive(false);
      setSearchQuery('Voice search coming soon...');
      onSearch('Voice search coming soon...');
    }, 2000);
  };

  const clearSearch = () => {
    setSearchQuery('');
    onSearch('');
  };

  return (
    <div className="bg-card rounded-xl shadow-warm p-4 md:p-6 mb-6">
      <form onSubmit={handleSearch} className="relative">
        <div className="flex items-center space-x-3">
          <div className="relative flex-1">
            <Icon
              name="MagnifyingGlassIcon"
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search stories by title, creator, or keyword..."
              className="w-full pl-12 pr-12 py-3.5 bg-input border border-border rounded-lg text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors duration-200"
              >
                <Icon name="XMarkIcon" size={20} />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={handleVoiceSearch}
            disabled={isVoiceSearchActive}
            className={`flex items-center justify-center w-12 h-12 rounded-lg transition-all duration-200 ${
              isVoiceSearchActive
                ? 'bg-primary text-primary-foreground animate-pulse'
                : 'bg-secondary text-secondary-foreground hover:bg-secondary/90'
            }`}
            aria-label="Voice search"
          >
            <Icon name="MicrophoneIcon" size={24} />
          </button>

          <button
            type="submit"
            className="hidden md:flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary-dark transition-all duration-200"
          >
            <Icon name="MagnifyingGlassIcon" size={20} />
            <span>Search</span>
          </button>
        </div>
      </form>

      <div className="flex flex-wrap gap-2 mt-4">
        <span className="text-sm text-text-secondary">Trending:</span>
        {['Love Stories', 'Thriller', 'Hindi Podcasts', 'Mythology'].map(
          (tag) => (
            <button
              key={tag}
              onClick={() => {
                setSearchQuery(tag);
                onSearch(tag);
              }}
              className="px-3 py-1 text-sm bg-muted text-text-primary rounded-full hover:bg-primary hover:text-primary-foreground transition-all duration-200"
            >
              {tag}
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default SearchBar;